/**
 * 用户名称处理工具函数
 */

/**
 * 截断长邮箱地址用于显示
 * @param {string} email - 邮箱地址
 * @param {number} maxLength - 最大长度，默认15
 * @returns {string} 截断后的邮箱地址
 */
export const truncateEmail = (email, maxLength = 15) => {
  if (!email) return '';
  
  if (email.length <= maxLength) {
    return email;
  }
  
  const [localPart, domain] = email.split('@');
  if (!domain) return email;
  
  // 如果本地部分太长，截断它
  if (localPart.length > maxLength - domain.length - 1) {
    const truncatedLocal = localPart.substring(0, maxLength - domain.length - 4) + '...';
    return `${truncatedLocal}@${domain}`;
  }
  
  return email;
};

/**
 * 生成默认用户名称
 * @param {Object} user - 用户对象
 * @returns {string} 用户名称
 */
export const generateDefaultUserName = (user) => {
  if (!user) return '用户';
  
  // 如果用户已有名称，直接返回
  if (user.name) {
    return user.name;
  }
  
  // 根据登录类型生成默认名称
  switch (user.loginType) {

    case 'phone':
      return user.phone ? `用户${user.phone.slice(-4)}` : '手机用户';
    case 'email':
      return user.email ? truncateEmail(user.email) : '邮箱用户';
    default:
      return user.phone ? `用户${user.phone.slice(-4)}` : '用户';
  }
};

/**
 * 获取用户头像显示字符
 * @param {Object} user - 用户对象
 * @returns {string} 头像显示字符
 */
export const getUserAvatarChar = (user) => {
  if (!user) return 'U';
  
  const userName = generateDefaultUserName(user);
  return userName.charAt(0) || 'U';
};

/**
 * 格式化用户信息用于显示
 * @param {Object} user - 用户对象
 * @returns {Object} 格式化后的用户信息
 */
export const formatUserInfo = (user) => {
  if (!user) {
    return {
      displayName: '用户',
      avatarChar: 'U',
      loginType: 'unknown'
    };
  }
  
  return {
    displayName: generateDefaultUserName(user),
    avatarChar: getUserAvatarChar(user),
    loginType: user.loginType || 'unknown',
    hasAvatar: !!user.avatar
  };
};

/**
 * 验证用户名称是否合适
 * @param {string} name - 用户名称
 * @returns {Object} 验证结果 {isValid: boolean, message: string}
 */
export const validateUserName = (name) => {
  if (!name || name.trim().length === 0) {
    return { isValid: false, message: '用户名称不能为空' };
  }
  
  if (name.length > 20) {
    return { isValid: false, message: '用户名称不能超过20个字符' };
  }
  
  // 检查是否包含敏感词汇（可以根据需要扩展）
  const sensitiveWords = ['admin', 'root', 'system', '管理员', '系统'];
  const lowerName = name.toLowerCase();
  for (const word of sensitiveWords) {
    if (lowerName.includes(word)) {
      return { isValid: false, message: '用户名称包含敏感词汇' };
    }
  }
  
  return { isValid: true, message: '' };
};